import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  TooltipProps,
} from 'recharts';
import { CountryPerformance } from '../services/api';
import { bloomberg } from '../theme/bloombergTheme';

interface SectorComparisonChartProps {
  sector: string;
  data: CountryPerformance[];
  height?: number;
}

interface ChartDataPoint {
  flag_name: string;
  country: string;
  country_name: string;
  market_name: string;
  change: number;
  market_cap: number;
  stock_count: number;
}

const formatMarketCap = (value: number): string => {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${value.toLocaleString()}`;
};

const getBarColor = (change: number): string => {
  if (change >= 4) return '#00703c';
  if (change >= 2) return '#00875a';
  if (change >= 0) return '#36b37e';
  if (change >= -2) return '#ff8f73';
  if (change >= -4) return '#de350b';
  return '#bf2600';
};

interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: Array<{
    payload: ChartDataPoint;
  }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper
        sx={{
          p: 1.5,
          backgroundColor: bloomberg.bg.secondary,
          border: `1px solid ${bloomberg.border.primary}`,
          boxShadow: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {data.country_name} ({data.market_name})
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: data.change >= 0 ? 'success.main' : 'error.main',
            fontWeight: 600,
          }}
        >
          {data.change >= 0 ? '+' : ''}{data.change.toFixed(2)}%
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Market Cap: {formatMarketCap(data.market_cap)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Stocks: {data.stock_count}
        </Typography>
      </Paper>
    );
  }
  return null;
};

const SectorComparisonChart: React.FC<SectorComparisonChartProps> = ({
  sector,
  data,
  height = 220,
}) => {
  // Filter out null values (countries without this sector) and transform data
  const chartData: ChartDataPoint[] = data
    .filter((d) => d.change !== null)
    .map((d) => ({
      flag_name: `${d.flag} ${d.market_name}`,
      country: d.country,
      country_name: d.country_name,
      market_name: d.market_name,
      change: d.change as number,
      market_cap: d.market_cap as number,
      stock_count: d.stock_count,
    }))
    .sort((a, b) => b.change - a.change);

  const missingCountries = data.filter((d) => d.change === null);

  // Calculate dynamic height based on number of bars
  const barHeight = 32;
  const dynamicHeight = Math.max(height, chartData.length * barHeight + 60);

  // Calculate domain with some padding
  const minChange = Math.min(...chartData.map((d) => d.change), 0);
  const maxChange = Math.max(...chartData.map((d) => d.change), 0);
  const padding = Math.max(Math.abs(minChange), Math.abs(maxChange)) * 0.15;

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
      }}
    >
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: bloomberg.text.primary }}>
        {sector}
      </Typography>

      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={dynamicHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={false} stroke="none" />
            <XAxis
              type="number"
              domain={[minChange - padding, maxChange + padding]}
              tickFormatter={(value) => `${value.toFixed(1)}%`}
              tick={{ fontSize: 11, fill: 'var(--bb-text-secondary)' }}
              axisLine={{ stroke: 'var(--bb-border-primary)' }}
            />
            <YAxis
              type="category"
              dataKey="flag_name"
              width={150}
              tick={{ fontSize: 11, fill: 'var(--bb-text-primary)' }}
              axisLine={{ stroke: 'var(--bb-border-primary)' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="change" radius={[0, 4, 4, 0]} maxBarSize={28}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.change)} />
              ))}
              <LabelList
                dataKey="change"
                position="right"
                formatter={(value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`}
                style={{ fontSize: 11, fontWeight: 500, fill: 'var(--bb-text-primary)' }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No data available for this sector
          </Typography>
        </Box>
      )}

      {missingCountries.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          * Not available in: {missingCountries.map((d) => d.market_name).join(', ')}
        </Typography>
      )}
    </Paper>
  );
};

export default SectorComparisonChart;
